import type { z } from 'zod';
import type { ArmourSlot, SkillId } from '../ids.ts';
import type { itemSchema, minigameSchema, recipeSchema, shopSchema } from '../schema.ts';

/**
 * Arms, armour, shields and tools, made from tables. Each table row is a material or a pattern, and every pair
 * becomes an item plus the forge activity or recipe that makes it, and a shop line if it is cheap
 * enough to buy. To add a metal, wood or kind of weapon, add a row; to rename one cell (an item
 * players already own), add it to LEGACY.
 *
 * Stats are worked out here, not typed in: damage is pattern × material, armour is the set's total
 * split across the five pieces, and value comes from the material. docs/DECISIONS.md #200.
 */

type Item = z.input<typeof itemSchema>;
type Recipe = z.input<typeof recipeSchema>;
type SmithingActivity = Extract<z.input<typeof minigameSchema>, { engine: 'smithing' }>['activities'][number];
type StockLine = z.input<typeof shopSchema>['stock'][number];

/** Shops charge this much over an item's value, so buying to haggle it back never pays. */
export const SHOP_MARKUP = 1.5;

// ── Weapons, shields and tools: forged at the Great Forge, one per metal × pattern ──────────────

/**
 * `damage` and `shield` multiply a pattern's damage and armour; `worth` is coin per ingot of value;
 * `ingots` multiplies how many a pattern takes and `strikes` adds hammer blows (the rare metals are
 * scarce and slow to work). `tool` is the bonus a tool of this metal gives its skill; none for bronze.
 */
const METALS = {
  bronze: { name: 'Bronze', ingot: 'bronze-ingot', damage: 1, shield: 0.8, level: 1, focus: 5, worth: 30, ingots: 1, strikes: 0, sold: true, note: 'Bronze: soft, but quick to sharpen.' },
  iron: { name: 'Iron', ingot: 'iron-ingot', damage: 1.35, shield: 1, level: 5, focus: 10, worth: 45, ingots: 1, strikes: 0, tool: 0.1, sold: true, note: 'Bloomery iron, hammered true.' },
  steel: { name: 'Steel', ingot: 'steel-ingot', damage: 1.75, shield: 1.2, level: 12, focus: 15, worth: 105, ingots: 1, strikes: 0, tool: 0.15, sold: false, note: 'Good steel, well tempered.' },
  damascus: { name: 'Damascus', ingot: 'damascus-ingot', damage: 2.67, shield: 1.35, level: 21, focus: 20, worth: 275, ingots: 2, strikes: 1, tool: 0.2, sold: false, note: 'Watered steel that holds an edge for ever.' },
  osmund: { name: 'Osmund', ingot: 'osmund-ingot', damage: 3.34, shield: 1.5, level: 64, focus: 22, worth: 400, ingots: 2, strikes: 2, sold: false, note: 'Northern iron, pure and tough.' },
  starIron: { name: 'Star-iron', ingot: 'star-iron-ingot', damage: 4.23, shield: 1.65, level: 84, focus: 24, worth: 600, ingots: 2, strikes: 3, sold: false, note: 'It catches light that isn’t there.' },
} as const satisfies Record<string, { name: string; ingot: string; damage: number; shield: number; level: number; focus: number; worth: number; ingots: number; strikes: number; tool?: number; sold: boolean; note: string }>;
type Metal = keyof typeof METALS;
type MetalRow = (typeof METALS)[Metal];

const WEAPONS = [
  { key: 'dagger', name: 'dagger', icon: 'plain-dagger', slot: 'offHand', damage: 6, accuracy: 0.1, ingots: 1, level: 0, description: 'Short and sharp, for the off hand.' },
  { key: 'sword', name: 'sword', icon: 'broadsword', slot: 'mainHand', damage: 9, accuracy: 0.05, ingots: 2, level: 1, description: 'A plain cross-hilted sword.' },
  { key: 'spear', name: 'spear', icon: 'barbed-spear', slot: 'mainHand', damage: 9, accuracy: 0, ingots: 1, extra: [{ item: 'oak-log', quantity: 1 }], level: 1, description: 'A leaf blade on an oak shaft.' },
  { key: 'axe', name: 'war axe', icon: 'battle-axe', slot: 'mainHand', damage: 10, accuracy: -0.03, ingots: 2, level: 2, description: 'A bearded head that bites through shields.' },
  { key: 'mace', name: 'mace', icon: 'spiked-mace', slot: 'mainHand', damage: 11, accuracy: -0.05, ingots: 3, level: 3, description: 'Heavy, blunt and honest.' },
] as const;

/** Off-hand shields: worn, not swung, they add their armour to the rest. */
const SHIELDS = [
  { key: 'buckler', name: 'buckler', icon: 'round-shield', armour: 0.04, ingots: 1, level: 0, description: 'A fist-sized boss and a rim: parry and punch.' },
  { key: 'heater-shield', name: 'heater shield', icon: 'templar-shield', armour: 0.07, ingots: 2, level: 2, description: 'Flat-topped, the shape of a flat-iron.' },
  { key: 'kite-shield', name: 'kite shield', icon: 'bordered-shield', armour: 0.09, ingots: 3, level: 4, description: 'Long enough to cover a rider’s leg.' },
] as const;

/**
 * Tools: held in the pack, the best one for a skill helps every game of it. Its metal's `tool`
 * bonus (scaled by quality) is added to the XP and, for goods that are graded, to the quality
 * score. A pattern may also be swung in a fight (`slot`, `damage`).
 */
const TOOLS = [
  { key: 'pick', name: 'pick', icon: 'war-pick', skill: 'mining', ingots: 2, level: 0, description: 'For following a seam.' },
  { key: 'smiths-hammer', name: 'smith’s hammer', icon: 'claw-hammer', skill: 'smithing', ingots: 2, level: 1, description: 'Balanced for a day at the anvil.' },
  { key: 'felling-axe', name: 'felling axe', icon: 'wood-axe', skill: 'woodcutting', slot: 'offHand', damage: 6, ingots: 2, level: 0, description: 'A long bit and an ash haft.' },
  { key: 'pick-set', name: 'pick set', icon: 'lockpicks', skill: 'lockpicking', ingots: 1, level: 2, description: 'Hooks, rakes and a tension wrench.' },
  { key: 'chisels', name: 'mason’s chisels', icon: 'chisel', skill: 'engineering', ingots: 1, level: 1, description: 'For dressing stone and cutting joints.' },
] as const;

/** Rods by wood, made at the Wharf: like tools, they help every cast. */
const RODS = {
  yew: { bonus: 0.1, log: 'yew-log', level: 8, worth: 60 },
  ash: { bonus: 0.15, log: 'ash-log', level: 30, worth: 200 },
  elm: { bonus: 0.2, log: 'elm-log', level: 45, worth: 320 },
} as const;

// ── Armour: five pieces a set, so a full set is head, body, hands, legs and feet ────────────────

/** Each piece's share of its set's armour, and how much material it takes. */
const PIECES: Record<ArmourSlot, { share: number; quantity: number; level: number }> = {
  hands: { share: 0.1, quantity: 1, level: 0 },
  feet: { share: 0.1, quantity: 1, level: 1 },
  head: { share: 0.2, quantity: 2, level: 2 },
  legs: { share: 0.2, quantity: 3, level: 4 },
  body: { share: 0.4, quantity: 6, level: 6 },
};

/**
 * `armour` is the whole set's; `worth` its value. Cloth and leather are sewn as recipes (no
 * minigame); mail and plate are forged. A full plate set (0.5) leaves room under the 60% cap.
 */
interface ArmourSet {
  armour: number;
  worth: number;
  level: number;
  focus: number;
  material: string;
  /** Material per piece, as a fraction of PIECES' quantity (scarce goods: one pelt a piece). */
  per?: number;
  /** A second material, as a fraction of PIECES' quantity. */
  with?: { item: string; per: number };
  sold: boolean;
  note: string;
  make: { forge: Metal } | { recipe: { venue: string; skills: [SkillId, SkillId] } };
  /** Each piece's name and icon. */
  pieces: Record<ArmourSlot, [string, string]>;
  /** The forge or recipe heading; the material's name if unset. */
  group?: string;
}

const ARMOURS: Record<string, ArmourSet> = {
  cloth: {
    armour: 0.25, worth: 62.5, level: 1, focus: 3, material: 'bolt-of-linen', sold: true,
    make: { recipe: { venue: 'market-cross', skills: ['haggling', 'illumination'] } },
    note: 'Quilted linen, stuffed with tow.',
    pieces: { head: ['Padded coif', 'hood'], body: ['Padded gambeson', 'leather-vest'], hands: ['Padded mittens', 'winter-gloves'], legs: ['Padded chausses', 'trousers'], feet: ['Padded shoes', 'walking-boot'] },
  },
  leather: {
    armour: 0.32, worth: 250, level: 5, focus: 5, material: 'tanned-hide', sold: true,
    make: { recipe: { venue: 'royal-chase', skills: ['hunting', 'horsemanship'] } },
    note: 'Boiled leather, hard as horn.',
    pieces: { head: ['Leather cap', 'light-helm'], body: ['Leather jerkin', 'leather-armor'], hands: ['Leather gloves', 'gloves'], legs: ['Leather leggings', 'armored-pants'], feet: ['Leather boots', 'leather-boot'] },
  },
  mail: {
    armour: 0.4, worth: 700, level: 8, focus: 10, material: 'iron-ingot', sold: false,
    make: { forge: 'iron' },
    note: 'Riveted iron rings, over padding.',
    pieces: { head: ['Mail coif', 'barbute'], body: ['Mail hauberk', 'chain-mail'], hands: ['Mail mittens', 'mailed-fist'], legs: ['Mail chausses', 'leg-armor'], feet: ['Mail sabatons', 'steeltoe-boots'] },
  },
  plate: {
    armour: 0.5, worth: 1500, level: 24, focus: 18, material: 'steel-ingot', sold: false,
    make: { forge: 'steel' },
    note: 'Steel plate, shaped to the body.',
    pieces: { head: ['Steel sallet', 'heavy-helm'], body: ['Steel breastplate', 'breastplate'], hands: ['Plate gauntlets', 'gauntlet'], legs: ['Plate cuisses', 'armor-cuisses'], feet: ['Plate sabatons', 'metal-boot'] },
  },
  // Plate in the rare metals: better at common quality, though fine steel plate already nears the cap.
  ...rarePlate('damascus', 0.54, 3500, 40),
  ...rarePlate('osmund', 0.57, 5000, 66),
  ...rarePlate('starIron', 0.6, 8000, 86),
  // Hunters' armour, from the Royal Chase's best trophies: a pelt a piece, lined with hide.
  wolfskin: {
    armour: 0.42, worth: 2400, level: 45, focus: 12, material: 'wolf-pelt', per: 1 / 6, with: { item: 'tanned-hide', per: 0.5 }, sold: false,
    make: { recipe: { venue: 'royal-chase', skills: ['hunting', 'horsemanship'] } },
    note: 'Grey wolf fur over boiled leather. Warm, light and tough.',
    pieces: { head: ['Wolfskin hood', 'wolf-head'], body: ['Wolfskin coat', 'fur-shirt'], hands: ['Wolfskin gloves', 'winter-gloves'], legs: ['Wolfskin leggings', 'armored-pants'], feet: ['Wolfskin boots', 'fur-boot'] },
  },
  bearskin: {
    armour: 0.48, worth: 3300, level: 65, focus: 14, material: 'bear-skin', per: 1 / 6, with: { item: 'tanned-hide', per: 0.5 }, sold: false,
    make: { recipe: { venue: 'royal-chase', skills: ['hunting', 'horsemanship'] } },
    note: 'A bear’s hide, thick enough to turn a blade.',
    pieces: { head: ['Bearskin hood', 'bear-head'], body: ['Bearskin coat', 'fur-shirt'], hands: ['Bearskin gloves', 'gloves'], legs: ['Bearskin leggings', 'armored-pants'], feet: ['Bearskin boots', 'fur-boot'] },
  },
};

function rarePlate(metal: 'damascus' | 'osmund' | 'starIron', armour: number, worth: number, level: number): Record<string, ArmourSet> {
  const m = METALS[metal];
  return {
    [metal]: {
      armour, worth, level, focus: m.focus, material: m.ingot, sold: false, make: { forge: metal }, group: `${m.name} plate`, note: `${m.name} plate. ${m.note}`,
      pieces: { head: [`${m.name} helm`, 'heavy-helm'], body: [`${m.name} breastplate`, 'breastplate'], hands: [`${m.name} gauntlets`, 'gauntlet'], legs: [`${m.name} cuisses`, 'armor-cuisses'], feet: [`${m.name} sabatons`, 'metal-boot'] },
    },
  };
}

// ── Bows: strung at the Butts, one per wood × pattern; arrows by the head ───────────────────────

/** `log` is what the stave is cut from; `level` the Woodcutting it takes to fell. */
/** `horn`: recurve and long bows of the better woods are nocked with hart's horn. */
const WOODS = {
  oak: { name: 'Oak', log: 'oak-log', damage: 0.85, worth: 0.6, level: 1, focus: 3, horn: false, sold: true },
  yew: { name: 'Yew', log: 'yew-log', damage: 1, worth: 1, level: 6, focus: 6, horn: false, sold: true },
  ash: { name: 'Ash', log: 'ash-log', damage: 1.4, worth: 2, level: 30, focus: 12, horn: true, sold: false },
  elm: { name: 'Elm', log: 'elm-log', damage: 1.75, worth: 3, level: 45, focus: 14, horn: true, sold: false },
  'bog-oak': { name: 'Bog oak', log: 'bog-oak-log', damage: 2.2, worth: 5, level: 65, focus: 16, horn: true, sold: false },
  heartwood: { name: 'Heartwood', log: 'heartwood-beam', damage: 2.7, worth: 8, level: 85, focus: 18, horn: true, sold: false },
} as const;

const BOWS = [
  { key: 'short-bow', name: 'short bow', icon: 'pocket-bow', damage: 8, accuracy: -0.05, worth: 35, level: 0, logs: 1, extra: [], description: 'Quick to draw, short in reach.' },
  { key: 'recurve-bow', name: 'recurve bow', icon: 'bow-arrow', damage: 10, accuracy: 0.05, worth: 80, level: 2, logs: 2, extra: [{ item: 'tanned-hide', quantity: 1 }], description: 'Tips bent back and bound with hide: it casts true.' },
  { key: 'longbow', name: 'longbow', icon: 'bow-string', damage: 12, accuracy: 0, worth: 160, level: 4, logs: 2, extra: [{ item: 'bag-of-nails', quantity: 1 }], description: 'Taller than its archer, and it draws like a mule.' },
] as const;

/** Arrowheads. A bow looses the best arrows in the pack; `damage` is added to each hit. */
const ARROWS = {
  bronze: { name: 'Bronze-tipped arrow', icon: 'broadhead-arrow', damage: 1, worth: 2, level: 1, sold: true, description: 'A barbed bronze broadhead.' },
  iron: { name: 'Iron-tipped arrow', icon: 'heavy-arrow', damage: 2, worth: 3, level: 5, sold: false, description: 'A heavy iron head that punches through leather.' },
  steel: { name: 'Steel bodkin', icon: 'barbed-arrow', damage: 3, worth: 5, level: 12, sold: false, description: 'A needle of steel, made for mail.' },
} as const satisfies Partial<Record<Metal, unknown>>;

/**
 * Items are keyed `<metal>-<pattern>`, `<wood>-<pattern>` or by the armour piece's name.
 * Generated cells that keep an older key, name or description, because players already hold them.
 * Their stats come out of the tables unchanged (the gambeson at 0.1, the short bow at 8).
 */
const LEGACY: Record<string, { key?: string; name?: string; description?: string }> = {
  'padded-gambeson': { description: 'Quilted linen. Better than nothing.' },
  'steel-breastplate': { description: 'A single plate over the chest, over mail.' },
  'yew-short-bow': { key: 'short-bow', name: 'Short bow', description: 'Yew, quick to draw.' },
  'bronze-dagger': { description: 'Leaf-bladed and heavy in the hand.' },
  'damascus-sword': { key: 'damascus-blade', name: 'Damascus blade' },
  'osmund-sword': { key: 'osmund-war-sword', name: 'Osmund war sword', description: 'A great sword of northern iron, long in the grip.' },
  'star-iron-sword': { key: 'star-iron-blade', name: 'Star-iron blade' },
  'iron-felling-axe': { key: 'iron-hatchet', name: 'Iron hatchet', description: 'Bearded head, ash haft.' },
};

// ── Expansion ───────────────────────────────────────────────────────────────────────────────────

/** The top tier everywhere else is 85; going past it would raise the cap on fight Renown (game-core MAX_ACTIVITY_LEVEL). */
const TOP_LEVEL = 85;
const lv = (n: number) => Math.min(TOP_LEVEL, n);
const round = (n: number, places = 3) => Math.round(n * 10 ** places) / 10 ** places;
const price = (value: number) => Math.ceil(value * SHOP_MARKUP);
const strikes = (ingots: number, m?: MetalRow) => Math.min(10, 3 + ingots + (m?.strikes ?? 0));
const cap = (s: string) => s[0]!.toUpperCase() + s.slice(1);
const slug = (s: string) => s.toLowerCase().replace(/[^a-z]+/g, '-');

const items: Item[] = [];
const forge: SmithingActivity[] = [];
const recipes: Recipe[] = [];
const armourer: StockLine[] = [];
const bowyer: StockLine[] = [];
/** Each armour's five pieces, by slot. */
const sets: Record<string, Record<ArmourSlot, string>> = {};

/** Add an item under its generated key, or its legacy one, and return the key used. */
function add(cell: string, item: Omit<Item, 'key'>, sold: boolean, shop: StockLine[]): string {
  const legacy = LEGACY[cell];
  const key = legacy?.key ?? cell;
  items.push({ ...item, key, name: legacy?.name ?? item.name, description: legacy?.description ?? item.description });
  if (sold && item.value) shop.push({ item: key, price: price(item.value) });
  return key;
}

/** A forge activity for one item of a metal. */
function forged(key: string, name: string, group: string, metal: Metal, level: number, ingots: number, extra: readonly { item: string; quantity: number }[] = []) {
  const m: MetalRow = METALS[metal];
  const blows = strikes(ingots, m);
  forge.push({ key, name, group, description: `${blows} hammer blows.`, level: lv(level), cost: { bar: 'focus', amount: m.focus }, inputs: [{ item: m.ingot, quantity: ingots }, ...extra], product: key, metal, strikes: blows });
}

for (const [metal, m] of Object.entries(METALS) as [Metal, MetalRow][]) {
  const prefix = slug(m.name);
  for (const w of WEAPONS) {
    const ingots = w.ingots * m.ingots;
    const name = `${m.name} ${w.name}`;
    const key = add(
      `${prefix}-${w.key}`,
      { name, icon: w.icon, kind: 'weapon', slot: w.slot, value: ingots * m.worth + ('extra' in w ? 10 : 0), combat: { damage: Math.round(w.damage * m.damage), ...(w.accuracy ? { accuracy: w.accuracy } : {}) }, description: `${w.description} ${m.note}` },
      m.sold,
      armourer,
    );
    forged(key, name, `${m.name} arms`, metal, m.level + w.level, ingots, 'extra' in w ? w.extra : []);
  }
  for (const sh of SHIELDS) {
    const ingots = sh.ingots * m.ingots;
    const name = `${m.name} ${sh.name}`;
    const key = add(`${prefix}-${sh.key}`, { name, icon: sh.icon, kind: 'armour', slot: 'offHand', value: ingots * m.worth, combat: { armour: round(sh.armour * m.shield) }, description: `${sh.description} ${m.note}` }, m.sold, armourer);
    forged(key, name, `${m.name} arms`, metal, m.level + sh.level, ingots);
  }
  if (!('tool' in m)) continue;
  for (const t of TOOLS) {
    const ingots = t.ingots * m.ingots;
    const name = `${m.name} ${t.name}`;
    const fights = 'slot' in t ? { slot: t.slot, combat: { damage: Math.round(t.damage * m.damage) } } : {};
    const key = add(`${prefix}-${t.key}`, { name, icon: t.icon, kind: 'tool', ...fights, tool: { skill: t.skill, bonus: m.tool }, value: ingots * m.worth, description: t.description }, false, armourer);
    forged(key, name, 'Tools', metal, m.level + t.level + 2, ingots);
  }
}

for (const [wood, r] of Object.entries(RODS)) {
  const w = WOODS[wood as keyof typeof WOODS];
  const name = `${w.name} fishing rod`;
  const key = add(`${wood}-fishing-rod`, { name, icon: 'fishing-pole', kind: 'tool', tool: { skill: 'fishing', bonus: r.bonus }, value: r.worth, description: `${w.name}, with a linen line. It helps every cast.` }, false, bowyer);
  recipes.push({
    key, name, group: 'Rods', venue: 'the-wharf', description: `A ${w.name.toLowerCase()} rod, and a line of twisted linen.`,
    skills: [{ skill: 'fishing', level: r.level }, { skill: 'woodcutting', level: Math.max(1, r.level - 2) }],
    cost: { bar: 'focus', amount: 4 + Math.round(r.bonus * 40) }, inputs: [{ item: r.log, quantity: 1 }, { item: 'bolt-of-linen', quantity: 1 }], product: { item: key, quantity: 1 },
  });
}

for (const [material, a] of Object.entries(ARMOURS)) {
  for (const [slot, piece] of Object.entries(PIECES) as [ArmourSlot, (typeof PIECES)[ArmourSlot]][]) {
    const [name, icon] = a.pieces[slot];
    const value = Math.round(a.worth * piece.share);
    const key = add(
      slug(name),
      { name, icon, kind: 'armour', slot, value, combat: { armour: round(a.armour * piece.share) }, description: a.note },
      a.sold,
      armourer,
    );
    (sets[material] ??= {} as Record<ArmourSlot, string>)[slot] = key;
    const level = lv(a.level + piece.level);
    const quantity = Math.max(1, Math.round(piece.quantity * (a.per ?? 1)));
    const lining = a.with ? [{ item: a.with.item, quantity: Math.max(1, Math.round(piece.quantity * a.with.per)) }] : [];
    const inputs = [{ item: a.material, quantity }, ...lining];
    const group = a.group ?? cap(material);
    if ('forge' in a.make) {
      forged(key, name, group, a.make.forge, level, quantity);
    } else {
      const { venue, skills } = a.make.recipe;
      recipes.push({
        key, name, group, venue, description: `${name}, from ${inputs.map((i) => `${i.quantity} × ${i.item.replace(/-/g, ' ')}`).join(' and ')}.`,
        skills: [{ skill: skills[0], level }, { skill: skills[1], level: Math.max(1, level - 2) }],
        cost: { bar: 'focus', amount: a.focus + piece.quantity }, inputs, product: { item: key, quantity: 1 },
      });
    }
  }
}

for (const [wood, t] of Object.entries(WOODS)) {
  for (const b of BOWS) {
    const name = `${t.name} ${b.name}`;
    const key = add(
      `${wood}-${b.key}`,
      { name, icon: b.icon, kind: 'weapon', slot: 'ranged', value: Math.round(b.worth * t.worth), combat: { damage: Math.round(b.damage * t.damage), ...(b.accuracy ? { accuracy: b.accuracy } : {}), ammo: 'arrow' }, description: b.description },
      t.sold,
      bowyer,
    );
    const level = lv(t.level + b.level);
    recipes.push({
      key, name, group: `${t.name} bows`, venue: 'the-butts', description: `${t.name} staves, ${b.logs > 1 ? `${b.logs} logs` : 'one log'}.`,
      skills: [{ skill: 'woodcutting', level }, { skill: 'archery', level: Math.max(1, level - 2) }],
      cost: { bar: 'focus', amount: t.focus + b.level },
      inputs: [{ item: t.log, quantity: b.logs }, ...b.extra, ...(t.horn && b.key !== 'short-bow' ? [{ item: 'hart-antlers', quantity: 1 }] : [])],
      product: { item: key, quantity: 1 },
    });
  }
}

bowyer.push({ item: 'arrow', price: price(1) });
for (const [metal, h] of Object.entries(ARROWS) as [keyof typeof ARROWS, (typeof ARROWS)[keyof typeof ARROWS]][]) {
  const key = add(`${metal}-arrow`, { name: h.name, icon: h.icon, kind: 'ammo', ammo: 'arrow', value: h.worth, combat: { damage: h.damage }, description: h.description }, h.sold, bowyer);
  recipes.push({
    key, name: `${h.name}s`, group: 'Arrows', venue: 'the-butts', description: 'Oak shafts, pheasant fletchings and twenty heads: a score of arrows.',
    skills: [{ skill: 'smithing', level: h.level }, { skill: 'archery', level: Math.max(3, h.level) }],
    cost: { bar: 'focus', amount: 4 + h.damage * 2 },
    inputs: [{ item: 'oak-log', quantity: 1 }, { item: 'brace-of-pheasants', quantity: 1 }, { item: METALS[metal].ingot, quantity: 1 }],
    product: { item: key, quantity: 20 },
  });
}

/** What the tables expand to. catalog.ts merges these into items, the forge, recipes and shops. */
export const gear = { items, forge, recipes, armourer, bowyer, sets };
